import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  selectHomeStatusItems,
  selectHomeSummaryCards,
} from "../../redux/selector";
import { useStyles } from "./styles";

const fallbackStatusItems = [
  {
    labelKey: "HOME.STATUS.LEAD_QUOTE",
    value: 2,
    color: "#3498DB",
  },
  {
    labelKey: "HOME.STATUS.WAITING_ENGINEERING",
    value: 8,
    color: "#9B59B6",
  },
  {
    labelKey: "HOME.STATUS.READY_PRODUCTION",
    value: 3,
    color: "#F39C12",
  },
  {
    labelKey: "HOME.STATUS.IN_PRODUCTION",
    value: 22,
    color: "#95A5A6",
  },
  {
    labelKey: "HOME.STATUS.QUALITY_CONTROL",
    value: 4,
    color: "#2ECC71",
  },
];

const fallbackSummaryCards = [
  {
    id: 1,
    value: 4,
    labelKey: "HOME.SUMMARY.DELIVERIES_TODAY",
    iconType: "delivery",
  },
  {
    id: 2,
    value: 12,
    labelKey: "HOME.SUMMARY.NEW_LEADS_THIS_WEEK",
    iconType: "leads",
  },
  {
    id: 3,
    value: 18,
    labelKey: "HOME.SUMMARY.ACTIVE_PRODUCTION_VEHICLES",
    iconType: "vehicles",
  },
  {
    id: 4,
    value: 7,
    labelKey: "HOME.SUMMARY.OPEN_ENGINEERING_TASKS",
    iconType: "engineering",
  },
];

const getIcon = (iconType: string) => {
  switch (iconType) {
    case "delivery":
      return <LocalShippingOutlinedIcon />;
    case "leads":
      return <PhoneOutlinedIcon />;
    case "vehicles":
      return <BuildOutlinedIcon />;
    case "engineering":
      return <HandymanOutlinedIcon />;
    default:
      return null;
  }
};

const WorkOrderStatusSummary = () => {
  const styles = useStyles();
  const { t } = useTranslation();

  const reduxStatusItems = useSelector(selectHomeStatusItems);
  const reduxSummaryCards = useSelector(selectHomeSummaryCards);

  const statusItems =
    reduxStatusItems && reduxStatusItems.length > 0
      ? reduxStatusItems
      : fallbackStatusItems;

  const summaryCards =
    reduxSummaryCards && reduxSummaryCards.length > 0
      ? reduxSummaryCards
      : fallbackSummaryCards;

  return (
    <Box className={styles.root}>
      <Box className={styles.statusCard}>
        <Box className={styles.statusContent}>
          <Box className={styles.legend}>
            {statusItems.map((item) => (
              <Box key={item.labelKey} className={styles.legendRow}>
                <Typography className={styles.legendValue}>
                  {item.value}
                </Typography>

                <Typography className={styles.legendLabel}>
                  {t(item.labelKey)}
                </Typography>

                <Box
                  className={styles.legendColor}
                  style={{ backgroundColor: item.color }}
                />
              </Box>
            ))}
          </Box>

          <Box className={styles.chartSection}>
            <Typography className={styles.title}>
              {t("HOME.WORK_ORDER_STATUS_TITLE")}
            </Typography>

            <Box className={styles.donutChart}>
              <Box className={styles.donutInner}>
                <Typography className={styles.donutNumber}>20</Typography>
                <Typography className={styles.donutText}>
                  {t("HOME.TOTAL")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className={styles.summaryGrid}>
        {summaryCards.map((card) => (
          <Box key={card.id} className={styles.summaryCard}>
            <Box className={styles.iconBox}>{getIcon(card.iconType)}</Box>

            <Box className={styles.summaryTextBox}>
              <Typography className={styles.cardValue}>{card.value}</Typography>
              <Typography className={styles.cardLabel}>
                {t(card.labelKey)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default WorkOrderStatusSummary;